import { StartPoint } from "@domain/StartPoint";
import { Plan } from "@dto/Plan";
import { PlanItem } from "@dto/PlanItem";
import dayjs from "dayjs";
import { t } from "i18next";

function positionComparator(
  a: { position: number },
  b: { position: number },
): number {
  const aPos = Number.isInteger(a.position)
    ? a.position
    : Number.MAX_SAFE_INTEGER;
  const bPos = Number.isInteger(b.position)
    ? b.position
    : Number.MAX_SAFE_INTEGER;
  return aPos - bPos;
}

interface Day {
  id: string;
  title: string;
}

interface DraggableFlatListItem {
  item: PlanItem | Day | StartPoint;
  type: "planItem" | "day" | "startPoint";
  dayIndex: number | null;
  distanceInMeters?: number;
  timeInMinutes?: number;
  stopIndex?: number;
}

/**
 * Parse date string like "2024-04-20" to "4/20"
 */
const parseDate = (value: string, addDays: number = 0) => {
  return dayjs(value).add(addDays, "day").format("M/DD");
};

const getPlanItemLocation = (planItem: PlanItem) => {
  const latitude = planItem?.plannable?.location?.latitude ?? null;
  const longitude = planItem?.plannable?.location?.longitude ?? null;
  if (latitude !== null && longitude !== null) {
    return { latitude, longitude };
  }
};

const getDayTitle = (dayIndex: number, startsOn: string | null): string => {
  if (startsOn) {
    return parseDate(startsOn, dayIndex - 1);
  }

  return `Day ${dayIndex}`;
};

const calculateListItems = (
  plan: Plan | undefined,
  planItems: PlanItem[],
  selectedDayValue: null | "all" | number,
  startingPoints: StartPoint[],
) => {
  if (!plan) {
    return [];
  }

  const dayDuration = plan.dayDuration ?? 0;

  planItems = [...planItems].sort(positionComparator);
  const items = [] as DraggableFlatListItem[];

  for (let dayIndex = 1; dayIndex <= dayDuration; ++dayIndex) {
    if (selectedDayValue !== dayIndex && selectedDayValue !== "all") {
      continue;
    }

    const title = getDayTitle(dayIndex, plan.startsOn);
    items.push({
      item: { id: `day-${dayIndex}`, title: title } as Day,
      dayIndex: dayIndex,
      type: "day",
    });

    const startingPoint = startingPoints.find((x) => x.dayIndex === dayIndex);
    if (startingPoint) {
      items.push({
        item: startingPoint,
        dayIndex: dayIndex,
        type: "startPoint",
      });
    }

    let stopIndex = 0;

    planItems.forEach((planItem, planItemIndex) => {
      if (planItem.dayIndex === dayIndex) {
        let planItemStopIndex = undefined as undefined | number;
        const location = getPlanItemLocation(planItem);
        const previousLocation = getPlanItemLocation(
          planItems[planItemIndex - 1],
        );

        if (
          location !== undefined &&
          (previousLocation === undefined ||
            (previousLocation.latitude !== location.latitude &&
              previousLocation.longitude !== location.longitude))
        ) {
          planItemStopIndex = ++stopIndex;
        }

        items.push({
          item: planItem,
          dayIndex: planItem.dayIndex,
          type: "planItem",
          stopIndex: planItemStopIndex,
        });
      }
    });
  }

  if (
    dayDuration > 0 &&
    (selectedDayValue === "all" || selectedDayValue === null)
  ) {
    items.push({
      item: {
        id: "unscheduled",
        title: t("screens.plan.unscheduledStops"),
      } as Day,
      dayIndex: null,
      type: "day",
    });
  } else {
    const startingPoint = startingPoints.find((x) => x.dayIndex === null);
    if (startingPoint) {
      items.push({
        item: startingPoint,
        dayIndex: null,
        type: "startPoint",
      });
    }
  }

  if (selectedDayValue === null || selectedDayValue === "all") {
    planItems.forEach((planItem) => {
      if (planItem.dayIndex === null) {
        items.push({
          item: planItem,
          dayIndex: null,
          type: "planItem",
        });
      }
    });
  }

  return items;
};

const calculatePlanItemUpdate = (
  update: DraggableFlatListItem[],
  toIndex: number,
) => {
  let newDayIndex = null as null | number;

  for (let i = toIndex - 1; i >= 0; --i) {
    if (update[i].type === "day") {
      newDayIndex = update[i].dayIndex;
      break;
    }
  }

  const maxPosition = update.reduce((aggregate, current) => {
    if (
      current.type === "planItem" &&
      aggregate < (current.item as PlanItem).position
    ) {
      return (current.item as PlanItem).position;
    }
    return aggregate;
  }, 0);

  let newPositionIndex = maxPosition + 1;

  for (let i = toIndex + 1; i < update.length; ++i) {
    if (update[i].type === "planItem") {
      newPositionIndex = (update[i].item as PlanItem).position;
      break;
    }
  }

  const updatedPlanItem = { ...update[toIndex].item } as PlanItem;
  update[toIndex].dayIndex = newDayIndex;
  updatedPlanItem.dayIndex = newDayIndex;
  updatedPlanItem.position = newPositionIndex;

  return updatedPlanItem;
};

export { calculateListItems, calculatePlanItemUpdate };
export type { Day, DraggableFlatListItem };
